/**
 * Validate a URL as a safe internal path before navigation.
 *
 * Notifications store a linkUrl string that is later used in client navigation
 * (router.push or <Link href>). Even though notifications are created by
 * server-side jobs (not user input), defense-in-depth: if an attacker ever
 * gained service-role access and inserted a notification with
 * linkUrl='https://evil.com' or 'javascript:alert(1)', this gate prevents
 * the exfil/XSS by restricting navigation to same-origin relative paths.
 *
 * Rules:
 *   - Must start with a single "/"
 *   - Must NOT start with "//" (protocol-relative URLs → external)
 *   - Must NOT start with "/\\" (backslash trick on Windows-parsing browsers)
 *   - Must NOT contain ":" before the first "/" (defends against "javascript:"
 *     and similar schemes that some parsers accept even without a leading /)
 *
 * Returns the path unchanged if safe, or null if rejected.
 */
export function safeInternalPath(path: string | null | undefined): string | null {
  if (!path) return null;
  if (typeof path !== 'string') return null;
  const trimmed = path.trim();
  if (trimmed.length === 0) return null;
  if (!trimmed.startsWith('/')) return null;
  if (trimmed.startsWith('//')) return null;
  if (trimmed.startsWith('/\\')) return null;
  // Reject any scheme indicator even when disguised with a leading slash
  // by some URL parsers (e.g. "/javascript:alert(1)").
  if (/^\/[a-z][a-z0-9+.-]*:/i.test(trimmed)) return null;
  return trimmed;
}
